import { useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Clock, SignIn, SignOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { hm, ukTime } from "./hrUtils";

/** Big clock in/out control with a live timer and this week's hours against contracted. Self only. */
export const ClockCard = ({ me, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 30000); return () => clearInterval(t); }, []);

  const open = me?.open_entry;
  const liveMinutes = open ? Math.max(0, Math.round((Date.now() - new Date(open.clock_in).getTime()) / 60000)) : 0;
  const week = me?.week || {};
  const todayMinutes = (week.days || []).find((d) => d.date === me?.today)?.minutes || 0;
  const weekTotal = week.total_minutes || 0;
  const contracted = week.contracted_minutes || 0;
  const pct = contracted ? Math.min(100, Math.round((weekTotal / contracted) * 100)) : 0;
  const over = weekTotal - contracted;

  const act = async (kind) => {
    setBusy(true);
    try {
      await API.post(`/hr/clock/${kind}`, {});
      toast.success(kind === "in" ? "Clocked in — have a good day" : `Clocked out · ${hm(liveMinutes)} today`);
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className={`rounded-xl border p-5 flex flex-col gap-4 ${open ? "border-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/20" : "border-border bg-card"}`} data-testid="hr-clock-card" data-tick={tick}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Time</p>
          <p className="text-3xl font-bold tracking-tight mt-1 tabular-nums" data-testid="hr-clock-today">{open ? hm(liveMinutes) : hm(todayMinutes)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {open ? `Clocked in at ${ukTime(open.clock_in)}` : todayMinutes ? "Clocked today — currently out" : "Not clocked in today"}
          </p>
        </div>
        <Clock className={`h-9 w-9 ${open ? "text-emerald-600" : "text-muted-foreground/50"}`} weight="duotone" />
      </div>
      {open ? (
        <Button size="lg" variant="outline" className="w-full border-emerald-400 text-emerald-800 hover:bg-emerald-100" disabled={busy} onClick={() => act("out")} data-testid="hr-clock-out-btn">
          <SignOut className="h-5 w-5 mr-2" /> Clock out
        </Button>
      ) : (
        <Button size="lg" className="w-full" disabled={busy} onClick={() => act("in")} data-testid="hr-clock-in-btn">
          <SignIn className="h-5 w-5 mr-2" /> Clock in
        </Button>
      )}
      <div>
        <div className="flex justify-between text-xs mb-1">
          <span className="text-muted-foreground">This week</span>
          <span className="font-medium tabular-nums" data-testid="hr-week-total">{hm(weekTotal)} / {hm(contracted)}</span>
        </div>
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div className={`h-full rounded-full transition-[width] duration-500 ${over > 0 ? "bg-amber-500" : "bg-primary"}`} style={{ width: `${pct}%` }} />
        </div>
        <p className={`text-[11px] mt-1 ${over > 0 ? "text-amber-700" : "text-muted-foreground"}`} data-testid="hr-week-delta">
          {contracted ? (over >= 0 ? `${hm(over)} over contracted hours` : `${hm(-over)} still to work this week`) : "No contracted hours set"}
        </p>
      </div>
    </div>
  );
};
